import { useState, useEffect } from 'react';
import { ArrowLeft, Star, Crown, TrendingUp, MapPin, Briefcase, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { API_URL } from '../lib/api-client';
import { toast } from 'sonner';

interface SponsoredProfile {
  _id: string;
  userId?: {
    _id: string;
  };
  firstName: string;
  lastName: string;
  age?: number;
  height?: string;
  city?: string;
  occupation?: string;
  photos?: any[];
  profilePhoto?: string;
  education?: string;
  religion?: string;
  about?: string;
}

interface ViewAllSponsoredProfilesProps {
  onBack: () => void;
  onViewProfile: (profileId: string, isSponsored: boolean) => void;
}

export function ViewAllSponsoredProfiles({ onBack, onViewProfile }: ViewAllSponsoredProfilesProps) {
  const [profiles, setProfiles] = useState<SponsoredProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchProfiles();
  }, [page]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(
        `${API_URL}/profiles/sponsored?page=${page}&limit=12`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      
      if (data.success) {
        const newProfiles = data.data.profiles || [];
        setProfiles(prev => page === 1 ? newProfiles : [...prev, ...newProfiles]);
        setTotal(data.data.total || 0);
        setHasMore(newProfiles.length === 12);
      }
    } catch (error) {
      console.error('Error fetching sponsored profiles:', error);
      toast.error('Failed to load sponsored profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = async (profile: SponsoredProfile) => {
    const profileId = profile._id;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view profiles');
        return;
      }

      const response = await fetch(`${API_URL}/profiles/sponsored/${profileId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        onViewProfile(profileId, true);
        
        if (data.data.creditDeducted) {
          toast.success(`1 credit used. ${data.data.remainingCredits || 0} credits remaining`, {
            duration: 3000
          });
        }
      } else {
        toast.error(data.message || 'Failed to view profile');
      }
    } catch (error: any) {
      console.error('Error viewing sponsored profile:', error);
      toast.error('Failed to view profile');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 rounded-lg transition-all duration-200 group"
            >
              <ArrowLeft className="size-5 group-hover:text-rose-600 transition-colors" />
            </button>
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <TrendingUp className="size-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Sponsored Profiles
                </h1>
                <p className="text-xs text-gray-500">{total} profiles boosting their visibility</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Info Banner */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="bg-gradient-to-r from-purple-100 via-pink-100 to-rose-100 border border-purple-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="size-8 rounded-lg bg-white/80 flex items-center justify-center flex-shrink-0">
              <Star className="size-4 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-900 mb-1">Featured Profiles</h3>
              <p className="text-sm text-purple-700">
                These profiles are currently boosted for maximum visibility. Viewing a sponsored profile uses 1 credit from your balance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Profiles Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {loading && page === 1 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="h-64 bg-gray-200 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-5 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-16">
            <div className="size-20 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="size-10 text-purple-500" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No Sponsored Profiles</h3>
            <p className="text-gray-600">There are no sponsored profiles at the moment.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {profiles.map((profile) => {
                const photoUrl = profile.photos?.[0] 
                  ? (typeof profile.photos[0] === 'string' ? profile.photos[0] : profile.photos[0]?.url)
                  : profile.profilePhoto || `https://ui-avatars.com/api/?name=${profile.firstName}+${profile.lastName}&background=f43f5e&color=fff`;

                return (
                  <div
                    key={profile._id}
                    onClick={() => handleProfileClick(profile)}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group relative"
                  >
                    {/* Sponsored Badge */}
                    <div className="absolute top-3 left-3 z-10">
                      <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 shadow-lg">
                        <Crown className="size-3 mr-1 fill-white" />
                        SPONSORED
                      </Badge>
                    </div>

                    {/* Photo */}
                    <div className="relative h-64 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                      <ImageWithFallback
                        src={photoUrl}
                        alt={`${profile.firstName} ${profile.lastName}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    {/* Profile Info */}
                    <div className="p-4">
                      <h3 className="font-semibold mb-2 truncate">
                        {profile.firstName} {profile.lastName?.charAt(0)}.
                      </h3>
                      
                      <div className="space-y-1.5 text-xs text-gray-600">
                        <div className="flex items-center gap-2">
                          <MapPin className="size-3 text-gray-400" />
                          <span className="truncate">
                            {profile.age || 25} Yrs, {profile.height || "5'5\""} • {profile.city || 'India'}
                          </span>
                        </div>
                        
                        {profile.occupation && (
                          <div className="flex items-center gap-2">
                            <Briefcase className="size-3 text-gray-400" />
                            <span className="truncate">{profile.occupation}</span>
                          </div>
                        )}
                        
                        {profile.education && (
                          <div className="flex items-center gap-2">
                            <GraduationCap className="size-3 text-gray-400" />
                            <span className="truncate">{profile.education}</span>
                          </div>
                        )}
                      </div>

                      <Button
                        className="w-full mt-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                        size="sm"
                      >
                        View Profile
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  onClick={() => setPage(p => p + 1)}
                  disabled={loading}
                  variant="outline"
                  className="border-purple-200 text-purple-600 hover:bg-purple-50"
                >
                  {loading ? 'Loading...' : 'Load More Profiles'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
